import { useEffect, useState } from "react";
import { Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import useDelete from "../../../global/hooks/useDelete";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { GlobalTable } from "../../../global/components/GlobalTable";
import ConfirmModal from "../../../global/components/ConfirmModal";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import { formatUTCTimeWithSec } from "../../../global/utils/unixToLocalTime";
import DeviceIcon from "../../../assets/images/vectors/SideBarIcons/DeviceIcon";
import { DeviceProductionItemsInfoColumns } from "../Page/DeviceProductionItemsInfoColumns";
import DeviceProductionModal from "./DeviceProductionModal";
import { getDeviceProductionListPath } from "../utils/listSearchStorage";

export const DeviceProductionForm = ({ type }) => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState({
    addByCountModal: false,
    addByListModal: false,
  });
  const [selectedItemKeys, setSelectedItemKeys] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMainDeleteModal, setShowMainDeleteModal] = useState(false);

  const isInfo = type === "info";
  const isEdit = type === "edit";
  const isAdd = !type || type === "add";

  // ── APIs ───────────────────────────────────────────────────────
  const detailUrl =
    (isInfo || isEdit) && id
      ? `${process.env.REACT_APP_API_URL}/api/device-production/${id}/`
      : null;

  const postUrl = `${process.env.REACT_APP_API_URL}/api/device-production/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/device-production/${id}/`;

  const itemsUrl =
    isInfo && id
      ? `${process.env.REACT_APP_API_URL}/api/device-production/${id}/items/`
      : null;

  // ── FIX: destructure refetch from detail useGet so we can call it after edit ──
  const {
    data: detailData,
    loading: detailLoading,
    refetch: detailRefetch,
  } = useGet(detailUrl);
  const {
    data: itemsData,
    loading: itemsLoading,
    refetch: itemsRefetch,
  } = useGet(itemsUrl);

  // Hardware options
  const hardwareUrl = `${process.env.REACT_APP_API_URL}/api/hardwares/?limit=100&order=desc`;
  const { data: hardwareData } = useGet(hardwareUrl);
  const hardwareOptions =
    hardwareData?.data?.map((item) => ({
      label: item.HardwareName,
      value: item.HardwareNo,
    })) || [];

  // License options
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/dealers/options/`;
  const { data: optionData } = useGet(optionUrl);
  const licenseOptions = getModifiedResponse(optionData, "licenses");

  // ── Same pattern as ProductionForm: destructure the action fn, not data/loading ──
  const { error: postError, postData: postAction } = usePost(postUrl);
  const { error: putError, putData: putAction } = usePut(putUrl);

  // ── Main Record Delete ─────────────────────────────────────────
  const mainDeleteUrl = `${process.env.REACT_APP_API_URL}/api/device-production/${id}/`;
  const {
    data: deletedMainData,
    error: deletedMainError,
    deleteData: deleteMainData,
  } = useDelete(mainDeleteUrl);

  // ── Delete item(s) ─────────────────────────────────────────────
  const deleteItemUrl = `${process.env.REACT_APP_API_URL}/api/device-production/${id}/items/bulk-delete/`;

  const {
    data: deletedItemData,
    error: deletedItemError,
    deleteMultiple: deleteItems,
  } = useDelete(deleteItemUrl);

  // ── Populate form ──────────────────────────────────────────────
  useEffect(() => {
    if (detailData?.data && (isInfo || isEdit)) {
      const d = detailData.data;
      form.setFieldsValue({
        ProductionName: d.ProductionName,
        ProductionDesc: d.ProductionDesc,
        HardwareNo: d.HardwareNo,
        LicenseNo: d.LicenseNo,
        ProductionTime: d.ProductionTime
          ? formatUTCTimeWithSec(d.ProductionTime)
          : "",
      });
    }
  }, [detailData, isInfo, isEdit, form]);

  // ── onFinish: same pattern as ProductionForm ───────────────────
  const onFinish = async (values) => {
    try {
      if (isEdit && id) {
        const result = await putAction(values);
        if (result != null) {
          openConfirmNotification("Success");
          // FIX: refetch detail data so info page shows updated values immediately
          await detailRefetch();
          setTimeout(() => {
            navigate(`/device-production/info/${id}`);
          }, 200);
        }
      } else if (isAdd) {
        const result = await postAction(values);
        if (result != null) {
          openConfirmNotification("Success");
          setTimeout(() => {
            navigate("/device-production");
          }, 200);
        }
      }
    } catch {
      openErrorNotification("Something went wrong.");
    }
  };

  // ── Error toasts for post/put (same as ProductionForm) ────────
  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [postError]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [putError]);

  // ── Main Record Delete ─────────────────────────────────────────
  const onMainDeleteConfirm = async () => {
    try {
      await deleteMainData();
    } catch {
      openErrorNotification("Something went wrong.");
    }
    setShowMainDeleteModal(false);
  };

  const onDeleteItemConfirm = async () => {
    try {
      await deleteItems({ ids: selectedItemKeys });
    } catch {
      openErrorNotification("Something went wrong.");
    }
    setShowDeleteModal(false);
    setSelectedItemKeys([]);
  };

  // Toast handlers for delete
  useEffect(() => {
    if (deletedMainData && !deletedMainError) {
      openConfirmNotification("Success");
      navigate(`/device-production/info/${id}`);
    } else if (deletedMainError) {
      openErrorNotification(
        deletedMainError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedMainData, deletedMainError]);

  useEffect(() => {
    if (deletedItemData && !deletedItemError) {
      openConfirmNotification("Success");
      itemsRefetch();
    } else if (deletedItemError) {
      openErrorNotification(
        deletedItemError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedItemData, deletedItemError, itemsRefetch]);

  const breadcrumbItems = [
    { label: "Device Production", href: "/device-production" },
    {
      label: isAdd ? "Add" : isEdit ? "Edit" : "Information",
      href: isAdd
        ? "/device-production/add"
        : isEdit
          ? `/device-production/edit/${id}`
          : `/device-production/info/${id}`,
    },
  ];

  // ==================== MAIN HEADER BUTTONS ====================
  const buttonsData = isInfo
    ? [
        {
          label: "Edit",
          icon: <EditOutlined />,
          onClick: () => navigate(`/device-production/edit/${id}`),
        },
        {
          label: "Delete",
          icon: <DeleteOutlined />,
          onClick: () => setShowMainDeleteModal(true),
        },
        {
          label: "List",
          icon: <UnorderedListOutlined />,
          onClick: () => navigate(getDeviceProductionListPath()),
        },
      ]
    : [
        {
          label: "Apply",
          icon: <CheckCircleOutlined />,
          onClick: () => form.submit(),
        },
        {
          label: "Cancel",
          icon: <CloseOutlined />,
          onClick: () => navigate(-1),
        },
      ];

  // ==================== DEVICES SUB-SECTION BUTTONS ====================
  const deviceProductionItemButtonsData = [
    {
      label: "Add by Count",
      icon: <PlusOutlined />,
      onClick: () => {
        setOpenModal((prev) => ({ ...prev, addByCountModal: true }));
      },
    },
    {
      label: "Add by List",
      icon: <PlusOutlined />,
      onClick: () => {
        setOpenModal((prev) => ({ ...prev, addByListModal: true }));
      },
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => setShowDeleteModal(true),
      disabled: selectedItemKeys?.length === 0,
    },
  ];

  // Server returns a flat array of devices directly under data[]
  const allDevicesData =
    itemsData?.data?.map((dev) => ({
      ...dev,
      key: dev?.DeviceNo ?? dev?.Mac,
      _licenseNo: detailData?.data?.LicenseNo,
    })) || [];

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>

          <div className="bg-white overflow-hidden border rounded-[4px]">
            <div className="flex items-center bg-gray-200 p-2.5">
              <DeviceIcon className="text-[18px] text-black" />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2 ">
                Device Production
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Name"
                    name="ProductionName"
                    disabled={isInfo}
                    required
                    loading={detailLoading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Description"
                    name="ProductionDesc"
                    disabled={isInfo}
                    loading={detailLoading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomSelect
                    label="Hardware"
                    name="HardwareNo"
                    options={hardwareOptions}
                    disabled={isInfo}
                    required
                    placeholder="Please select"
                    loading={detailLoading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomSelect
                    label="License"
                    name="LicenseNo"
                    options={licenseOptions}
                    disabled={isInfo}
                    required
                    placeholder="Please select"
                    loading={detailLoading}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Form>

      <ConfirmModal
        showModal={showMainDeleteModal}
        setShowModal={setShowMainDeleteModal}
        onDeleteConfirm={onMainDeleteConfirm}
        setSelectedRowKeys={() => {}}
      />
      <ConfirmModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        onDeleteConfirm={onDeleteItemConfirm}
        setSelectedRowKeys={setSelectedItemKeys}
      />

      {isInfo && (
        <>
          <div className="flex justify-end mb-2">
            <CommonButton buttonsData={deviceProductionItemButtonsData} />
          </div>
          <GlobalTable
            columns={DeviceProductionItemsInfoColumns()}
            dataSource={[...allDevicesData].sort(
              (a, b) => (b.DeviceNo ?? 0) - (a.DeviceNo ?? 0),
            )}
            rowSelection={{
              selectedRowKeys: selectedItemKeys,
              onChange: setSelectedItemKeys,
              fixed: true,
            }}
            onRow={() => ({})}
            loading={itemsLoading}
          />
          <DeviceProductionModal
            isOpenModal={openModal.addByCountModal}
            setIsOpenModal={(value) =>
              setOpenModal((prev) => ({ ...prev, addByCountModal: value }))
            }
            productionRefetch={itemsRefetch}
            modalType="addByCount"
            productionId={id}
            itemId={selectedItemKeys[0]}
            LicenseNo={detailData?.data?.LicenseNo}
            title={
              <div className="flex items-center bg-gray-200 p-2 rounded-md">
                <DeviceIcon className="text-[18px] text-black " />
                <span className="text-[14px] font-[Poppins] text-black ml-2">
                  Add by Count
                </span>
              </div>
            }
          />
          <DeviceProductionModal
            isOpenModal={openModal.addByListModal}
            setIsOpenModal={(value) =>
              setOpenModal((prev) => ({ ...prev, addByListModal: value }))
            }
            productionRefetch={itemsRefetch}
            modalType="addByList"
            productionId={id}
            itemId={selectedItemKeys[0]}
            LicenseNo={detailData?.data?.LicenseNo}
            title={
              <div className="flex items-center bg-gray-200 p-2 rounded-md">
                <DeviceIcon className="text-[18px] text-black " />
                <span className="text-[14px] font-[Poppins] text-black ml-2">
                  Add by List
                </span>
              </div>
            }
          />
        </>
      )}
    </>
  );
};
